/**
 * ENG-10869 — where `.claude.json` lives. The lower half of the locator.
 *
 * Path resolution lives HERE, in core, rather than only in the CLI, because
 * `ensureWorkspaceTrusted` in this same directory WRITES that file — atomically,
 * temp-then-rename, on the manager's tick. A second hand-kept copy of the rule
 * would have drifted the moment the CLI's copy moved, and the symptom would be
 * the trust flag written to a file no agent reads: every new agent back on the
 * trust prompt, with the write succeeding. The CLI's `claude-config-location`
 * re-exports these and adds the seeding and readiness checks on top, so there is
 * one implementation of the path and one of the migration.
 *
 * Worth recording while it is fresh: `claude-json-health.ts` states that "an
 * exhaustive search of apps/cli, packages/core and packages/api finds no write
 * and no unlink of `.claude.json`". That is not correct — `ensureWorkspaceTrusted`
 * is a writer, and it is in the set that search covered. The conclusion drawn
 * from it (that there is no call site to make atomic) happens to hold anyway,
 * because that writer was already atomic; but the premise should not be relied
 * on again.
 */

import { lstatSync, readFileSync } from 'node:fs';
import { homedir } from 'node:os';
import { join } from 'node:path';

/** The env var Claude Code reads for its config root. Not ours; do not rename. */
export const CLAUDE_CONFIG_DIR_ENV = 'CLAUDE_CONFIG_DIR';

/**
 * The config root Claude Code will use.
 *
 * A blank value is treated as unset rather than as the empty path: `''` would
 * make `join` produce a path relative to the process cwd, which is the same
 * class of trap ENG-5070 hit with `HOME=''` — broken, but better hidden than an
 * outright failure.
 */
export function claudeConfigDirFor(
  homeDir?: string,
  env: NodeJS.ProcessEnv = process.env,
): string {
  const explicit = env[CLAUDE_CONFIG_DIR_ENV]?.trim();
  if (explicit) return explicit;
  return join(homeDir || env.HOME?.trim() || homedir(), '.claude');
}

/** The `.claude.json` a Claude Code process will actually read and write. */
export function claudeJsonPathFor(homeDir?: string, env: NodeJS.ProcessEnv = process.env): string {
  return join(claudeConfigDirFor(homeDir, env), '.claude.json');
}

/**
 * Where `.claude.json` lived before ENG-10869: beside the config dir rather
 * than inside it, which is why it needed a single-file bind mount and why that
 * bind pinned each container's inode.
 */
export function legacyClaudeJsonPathFor(
  homeDir?: string,
  env: NodeJS.ProcessEnv = process.env,
): string {
  return join(homeDir || env.HOME?.trim() || homedir(), '.claude.json');
}

/**
 * ENG-10869 — what state the config at `path` is in.
 *
 * ## Why "parses" was the wrong test
 *
 * The first version of this change gated the whole migration on "the file
 * exists and `JSON.parse` succeeds". Two reviewers found the same hole from
 * opposite ends, and they were both right:
 *
 *  - `ensureWorkspaceTrusted` (this very directory) CREATES the file when a
 *    project needs its trust flag. That file is `{"projects":{…}}` — it parses
 *    perfectly, and it contains no account at all. So the manager could
 *    manufacture a config that looked ready, the spawn would drop the legacy
 *    bind on the strength of it, and every agent on the host would come up on
 *    the login picker. The fix's own writer could cause the outage the fix
 *    exists to prevent.
 *  - The same weakness made the documented recovery useless: an operator
 *    running `claude /login` writes the LEGACY path, the seed saw a parseable
 *    target and declared `already-present`, and the fresh account never
 *    reached an agent.
 *
 * So readiness means **an agent can actually start from this file**, which
 * requires account-bearing state, not merely valid JSON.
 *
 * ## The rule is `hasCompletedOnboarding`, and PRODUCTION chose it
 *
 * The first version accepted `oauthAccount` **or** `hasCompletedOnboarding`, on
 * the reasoning that either proves an account. agt-aws-1 disproved it on
 * 2026-09-22 while this change was still in review:
 *
 *     host /root/.claude.json   27 keys, PARSES, oauthAccount present,
 *                               hasCompletedOnboarding ABSENT
 *     -> all 8 agents sitting on the Claude Code login picker
 *
 * Under the OR that config reads as `usable`, the spawn drops the legacy bind
 * on the strength of it, and every agent on the host is left on the picker with
 * no fallback — the exact outage this file exists to prevent, caused by it.
 *
 * **What the evidence does and does not establish.** The successful re-pair that
 * recovered the host wrote BOTH a new `hasCompletedOnboarding: true` and fresh
 * credentials, so this is a correlation, not a proof that the flag alone is
 * what Claude Code gates on. The rule is chosen on the ASYMMETRY rather than on
 * the causal claim:
 *
 *   - Requiring too MUCH costs a migration that does not happen. The host stays
 *     on the legacy bind, which is exactly today's behaviour.
 *   - Requiring too LITTLE costs every agent on the host, with the legacy file
 *     already archived out from under them.
 *
 * So it requires the onboarding flag, and `oauthAccount` is no longer
 * sufficient on its own. This also lands close to where a reviewer's instinct
 * pointed on #6368; the counter-argument made there was weaker than it read.
 *
 * ## Symlinks are refused, not followed
 *
 * `.claude.json` now lives inside a directory the agent can write. A compromised
 * agent could replace it with a symlink to a file outside its own mounts — say
 * another agent's `.mcp.json` — and this function runs as the host manager. A
 * read that follows it, followed by a write-back, copies private content into a
 * shared mount. The single-file bind made that impossible before, so the
 * relocation is what introduces the exposure and the refusal belongs here, at
 * the one place both the reader and the writer consult.
 */
export type ClaudeConfigState =
  /** Nothing at the path. */
  | 'missing'
  /** A symlink. Deliberately not followed — see the docblock. */
  | 'symlink'
  /** Present but not a readable, parseable regular file (includes a DIRECTORY). */
  | 'unreadable'
  /** Parses, but carries no account — an agent would land on the login picker. */
  | 'no-account'
  /** An agent can start from this. */
  | 'usable';

export function inspectClaudeConfig(path: string): ClaudeConfigState {
  let stat: ReturnType<typeof lstatSync>;
  try {
    stat = lstatSync(path);
  } catch {
    return 'missing';
  }
  // lstat, never stat: the point is to SEE the symlink rather than resolve it.
  if (stat.isSymbolicLink()) return 'symlink';
  if (!stat.isFile()) return 'unreadable';
  let parsed: unknown;
  try {
    parsed = JSON.parse(readFileSync(path, 'utf-8'));
  } catch {
    return 'unreadable';
  }
  if (typeof parsed !== 'object' || parsed === null) return 'unreadable';
  const config = parsed as Record<string, unknown>;
  // `=== true`, not truthiness: a config carrying `hasCompletedOnboarding: 1`
  // or `"false"` is a shape nobody writes, and guessing at it is how a rule
  // that must fail safe starts admitting things.
  return config['hasCompletedOnboarding'] === true ? 'usable' : 'no-account';
}

/** mtime in ms, or 0 when the path cannot be stat'ed. Never follows a symlink. */
export function claudeConfigMtimeMs(path: string): number {
  try {
    const stat = lstatSync(path);
    return stat.isFile() ? stat.mtimeMs : 0;
  } catch {
    return 0;
  }
}

/**
 * ENG-10869 — the `.claude.json` an agent on this host will ACTUALLY read.
 *
 * The migration is per-host and conditional: the spawn path hands a container
 * `CLAUDE_CONFIG_DIR` only once the config dir holds a usable file, and
 * otherwise falls back to binding the legacy path. So there are hosts, for as
 * long as the seed has not landed, where the legacy file is still the live one.
 *
 * Anything that WRITES this file has to resolve it the same way, or it writes
 * somewhere nothing reads. That is not hypothetical: `ensureWorkspaceTrusted`
 * was moved to the config dir unconditionally while the reader stayed
 * conditional, which on any host the seed missed put the trust flag in a file no
 * agent opens — the write succeeding, and every new agent going back to dying on
 * the trust prompt (the ENG-9573 incident, restored behind green monitoring).
 *
 * One function, consulted by both sides, is what keeps them from disagreeing.
 */
export function effectiveClaudeJsonPath(
  homeDir?: string,
  env: NodeJS.ProcessEnv = process.env,
): string {
  const inConfigDir = claudeJsonPathFor(homeDir, env);
  if (inspectClaudeConfig(inConfigDir) === 'usable') return inConfigDir;
  return legacyClaudeJsonPathFor(homeDir, env);
}
